(function () {
  const root = document.documentElement;
  const sectionSelector = [
    '.content-for-layout > .shopify-section > section',
    '.content-for-layout > .shopify-section > div',
    '.content-for-layout > .shopify-section > footer',
    '.shopify-section-group-footer-group > .shopify-section > footer',
    '.wubs-agency-footer',
    '.wubs-agency-article__related',
  ].join(',');
  const excludedSelector = [
    '.wubs-agency-hero',
    '.wubs-agency-video-cta',
    '.wubs-agency-project-showcase',
    '.wubs-agency-logo-marquee',
    '.shopify-section-header-sticky',
  ].join(',');

  const state = new WeakMap();
  let active = null;
  let frame = 0;
  let lastPointerX = 0;
  let lastPointerY = 0;
  let hasLastPointer = false;

  function parseRgb(value) {
    const match = value && value.match(/rgba?\(([^)]+)\)/);
    if (!match) return null;
    const parts = match[1].split(',').map((part) => Number.parseFloat(part));
    if (parts.length < 3 || (parts.length > 3 && parts[3] === 0)) return null;
    return parts;
  }

  function brightness(parts) {
    return (parts[0] * 299 + parts[1] * 587 + parts[2] * 114) / 1000;
  }

  function sectionColor(section) {
    let node = section;

    while (node && node !== document.documentElement) {
      const color = parseRgb(window.getComputedStyle(node).backgroundColor);
      if (color) {
        const isDark = brightness(color) < 95;
        return isDark
          ? { r: 255, g: 255, b: 255, a: 0.085, isDark }
          : { r: 0, g: 0, b: 0, a: 0.052, isDark };
      }
      node = node.parentElement;
    }

    return { r: 0, g: 0, b: 0, a: 0.052, isDark: false };
  }

  function prepareSection(section) {
    if (section.matches(excludedSelector) || state.has(section)) return;

    const color = sectionColor(section);
    const isFooter = section.classList.contains('wubs-agency-footer');
    if (!isFooter && !color.isDark) return;

    section.classList.add('wubs-grid-section');
    section.style.setProperty('--wubs-grid-r', color.r);
    section.style.setProperty('--wubs-grid-g', color.g);
    section.style.setProperty('--wubs-grid-b', color.b);
    section.style.setProperty('--wubs-grid-alpha', color.a);
    updateGridOffset(section);

    state.set(section, {
      color,
      canvas: null,
      ctx: null,
      width: 0,
      height: 0,
      targetX: 0,
      targetY: 0,
      smoothX: 0,
      smoothY: 0,
      intensity: 0,
      targetIntensity: 0,
      entered: false,
    });

    section.addEventListener('pointerenter', onEnter, { passive: true });
    section.addEventListener('pointermove', onMove, { passive: true });
    section.addEventListener('pointerleave', onLeave, { passive: true });
  }

  function collectSections() {
    document.querySelectorAll(sectionSelector).forEach(prepareSection);
  }

  function updateGridOffset(section) {
    const rect = section.getBoundingClientRect();
    const documentX = rect.left + window.scrollX;
    const documentY = rect.top + window.scrollY;
    section.style.setProperty('--wubs-grid-bg-x', `${-(documentX % 48)}px`);
    section.style.setProperty('--wubs-grid-bg-y', `${-(documentY % 48)}px`);
    section.style.setProperty('--wubs-footer-grid-x', `${-(documentX % 48)}px`);
    section.style.setProperty('--wubs-footer-grid-y', `${-(documentY % 48)}px`);
  }

  function ensureCanvas(section, data) {
    if (data.canvas) return;

    const canvas = document.createElement('canvas');
    canvas.className = 'wubs-grid-canvas';
    canvas.setAttribute('aria-hidden', 'true');
    section.prepend(canvas);
    data.canvas = canvas;
    data.ctx = canvas.getContext('2d', { alpha: true });
  }

  function resize(section, data) {
    const rect = section.getBoundingClientRect();
    const width = Math.max(1, Math.round(rect.width));
    const height = Math.max(1, Math.round(rect.height));
    const scale = Math.min(window.devicePixelRatio || 1, 1.5);

    if (data.width !== width || data.height !== height) {
      data.width = width;
      data.height = height;
      data.canvas.width = Math.round(width * scale);
      data.canvas.height = Math.round(height * scale);
      data.canvas.style.width = `${width}px`;
      data.canvas.style.height = `${height}px`;
      data.ctx.setTransform(scale, 0, 0, scale, 0, 0);
    }

    return rect;
  }

  function updatePointer(section, event) {
    const data = state.get(section);
    if (!data) return;

    const rect = section.getBoundingClientRect();
    const previousPointerX = lastPointerX;
    const previousPointerY = lastPointerY;
    const hadPreviousPointer = hasLastPointer;

    lastPointerX = event.clientX;
    lastPointerY = event.clientY;
    hasLastPointer = true;

    data.targetX = event.clientX - rect.left;
    data.targetY = event.clientY - rect.top;

    if (!data.entered) {
      data.smoothX = hadPreviousPointer ? previousPointerX - rect.left : data.targetX;
      data.smoothY = hadPreviousPointer ? previousPointerY - rect.top : data.targetY;
      data.entered = true;
    }
  }

  function onEnter(event) {
    const section = event.currentTarget;
    const data = state.get(section);
    if (!data) return;

    ensureCanvas(section, data);
    active = section;
    if (!data.entered) data.intensity = Math.min(data.intensity, 0.26);
    data.targetIntensity = 1;
    section.classList.add('is-wubs-grid-active');
    updatePointer(section, event);
    startDrawing();
  }

  function onMove(event) {
    updatePointer(event.currentTarget, event);
    startDrawing();
  }

  function onLeave(event) {
    const section = event.currentTarget;
    const data = state.get(section);
    section.classList.remove('is-wubs-grid-active');
    if (data) {
      lastPointerX = event.clientX;
      lastPointerY = event.clientY;
      hasLastPointer = true;
      data.entered = false;
      data.targetIntensity = 0;
    }
    if (active === section) active = null;
  }

  function warp(value, pointer, radius, strength) {
    const distance = value - pointer;
    const abs = Math.abs(distance);
    if (abs >= radius) return value;

    const progress = abs / radius;
    const influence = Math.pow(1 - progress, 2.15);
    return value + (distance / radius) * influence * strength * 3.4;
  }

  function draw(section, data) {
    const rect = resize(section, data);
    const ctx = data.ctx;
    const width = data.width;
    const height = data.height;
    const base = 48;
    const radius = 340;
    const strength = 78;
    const documentX = rect.left + window.scrollX;
    const documentY = rect.top + window.scrollY;
    const startX = -base + ((-documentX) % base);
    const startY = -base + ((-documentY) % base);

    data.smoothX += (data.targetX - data.smoothX) * 0.095;
    data.smoothY += (data.targetY - data.smoothY) * 0.095;
    data.intensity += (data.targetIntensity - data.intensity) * 0.095;

    if (Math.abs(data.targetIntensity - data.intensity) < 0.001) {
      data.intensity = data.targetIntensity;
    }

    ctx.clearRect(0, 0, width, height);
    ctx.strokeStyle = `rgba(${data.color.r}, ${data.color.g}, ${data.color.b}, ${data.color.a})`;
    ctx.lineWidth = 1;
    ctx.beginPath();

    for (let x = startX; x <= width + base; x += base) {
      const warpedX = warp(x, data.smoothX, radius, strength * data.intensity);
      ctx.moveTo(Math.round(warpedX) + 0.5, 0);
      ctx.lineTo(Math.round(warpedX) + 0.5, height);
    }

    for (let y = startY; y <= height + base; y += base) {
      const warpedY = warp(y, data.smoothY, radius, strength * data.intensity);
      ctx.moveTo(0, Math.round(warpedY) + 0.5);
      ctx.lineTo(width, Math.round(warpedY) + 0.5);
    }

    ctx.stroke();
  }

  function tick() {
    frame = 0;
    if (!active) return;

    const data = state.get(active);
    if (!data || !data.canvas) return;

    draw(active, data);

    if (
      Math.abs(data.smoothX - data.targetX) > 0.1 ||
      Math.abs(data.smoothY - data.targetY) > 0.1 ||
      Math.abs(data.intensity - data.targetIntensity) > 0.001
    ) {
      startDrawing();
    }
  }

  function startDrawing() {
    if (!frame) frame = window.requestAnimationFrame(tick);
  }

  function refreshColors() {
    document.querySelectorAll('.wubs-grid-section').forEach((section) => {
      const data = state.get(section);
      if (!data) return;

      updateGridOffset(section);
      data.color = sectionColor(section);
      section.style.setProperty('--wubs-grid-r', data.color.r);
      section.style.setProperty('--wubs-grid-g', data.color.g);
      section.style.setProperty('--wubs-grid-b', data.color.b);
      section.style.setProperty('--wubs-grid-alpha', data.color.a);
    });
  }

  function init() {
    if (window.matchMedia('(hover: none), (prefers-reduced-motion: reduce)').matches) return;
    root.classList.add('wubs-grid-ready');
    collectSections();

    window.addEventListener('resize', () => {
      refreshColors();
      startDrawing();
    }, { passive: true });
    window.addEventListener('load', refreshColors, { once: true });

    document.addEventListener('shopify:section:load', () => {
      collectSections();
      refreshColors();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
